'use client';

// Client-side auth island for the top nav. Reads the IndexedDB
// identity on mount and swaps the right-side buttons:
//   - identity present  → "Dashboard" link + truncated account_id
//   - no identity yet   → "Log in" + "Get started"
//
// Nav itself stays server-rendered (good for SEO + fast first paint);
// only this small island hydrates on the client. Initial render uses
// the logged-out state so SSR HTML matches client-pre-hydration —
// avoids a hydration-mismatch warning.

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { loadIdentity, shortAccountId } from '../../lib/identity';

export function NavAuthArea() {
  const [account, setAccount] = useState<string | null>(null);
  const [ready,   setReady]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadIdentity()
      .then((id) => { if (!cancelled) setAccount(id?.account_id ?? null); })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  // Pre-hydration: render the logged-out state. After IndexedDB
  // resolves, swap. The reserved-space approach (always rendering
  // both with one hidden) would avoid layout shift but make the
  // markup gnarlier — the hydration delta is small enough to skip it.
  if (!ready || !account) {
    return (
      <>
        <Link href="/login"  className="hidden sm:inline-flex text-sm text-ink-dim hover:text-ink px-3 py-1.5">
          Log in
        </Link>
        <Link href="/signup" className="btn-primary text-sm">
          Get started
        </Link>
      </>
    );
  }

  return (
    <>
      <span className="hidden sm:inline-flex text-xs text-ink-faint font-mono px-2"
            title={account}>
        {shortAccountId(account)}
      </span>
      <Link href="/dashboard" className="btn-primary text-sm">
        Dashboard
      </Link>
    </>
  );
}
